import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Mail, Send, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Field, PageHeader } from '@/components/ui/form-field';
import { Alert, Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Select, Skeleton, Switch } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

type Security = 'NONE' | 'STARTTLS' | 'TLS';

interface MailView {
  settings: {
    host: string;
    port: number;
    security: Security;
    username: string | null;
    hasPassword: boolean;
    fromAddress: string;
    fromName: string | null;
    replyTo: string | null;
    verifyTls: boolean;
    status: 'UNTESTED' | 'OK' | 'ERROR';
    lastError: string | null;
    lastTestAt: string | null;
    updatedAt: string;
  } | null;
  route: 'm365' | 'smtp' | 'platform' | 'none';
  isPlatform: boolean;
}

const KEY = ['tenant', 'mail-settings'];
const onError = (e: unknown) => toast.error(errorMessage(e));

const ROUTE_TEXT: Record<MailView['route'], { text: string; variant: 'success' | 'default' | 'secondary' | 'destructive' }> = {
  m365: { text: 'Versand über Microsoft 365 (Standard-Absenderpostfach); SMTP dient als Rückfall', variant: 'success' },
  smtp: { text: 'Versand über den SMTP-Server unten', variant: 'success' },
  platform: { text: 'Versand über den SMTP-Server der Plattform (Vorgabe)', variant: 'secondary' },
  none: { text: 'Kein Mailversand eingerichtet – Einladungen, Benachrichtigungen und Passwort-Links werden nicht zugestellt', variant: 'destructive' },
};

const PRESETS: { label: string; host: string; port: number; security: Security }[] = [
  { label: 'Microsoft 365 / Exchange Online (SMTP AUTH)', host: 'smtp.office365.com', port: 587, security: 'STARTTLS' },
  { label: 'IONOS', host: 'smtp.ionos.de', port: 587, security: 'STARTTLS' },
  { label: 'Strato', host: 'smtp.strato.de', port: 465, security: 'TLS' },
];

interface Form {
  host: string;
  port: number;
  security: Security;
  username: string;
  password: string;
  fromAddress: string;
  fromName: string;
  replyTo: string;
  verifyTls: boolean;
}

export function MailSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: KEY, queryFn: () => api<MailView>('/tenant/mail-settings') });
  const [form, setForm] = useState<Form>({ host: '', port: 587, security: 'STARTTLS', username: '', password: '', fromAddress: '', fromName: '', replyTo: '', verifyTls: true });
  const [testTo, setTestTo] = useState('');
  const [confirmDialog, confirm] = useConfirm();
  const s = data?.settings;

  useEffect(() => {
    if (s) setForm({ host: s.host, port: s.port, security: s.security, username: s.username ?? '', password: '', fromAddress: s.fromAddress, fromName: s.fromName ?? '', replyTo: s.replyTo ?? '', verifyTls: s.verifyTls });
  }, [s]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () =>
      api<MailView>('/tenant/mail-settings', {
        method: 'PUT',
        body: {
          host: form.host.trim(),
          port: form.port,
          security: form.security,
          username: form.username.trim() || null,
          ...(form.password ? { password: form.password } : {}),
          fromAddress: form.fromAddress.trim(),
          fromName: form.fromName.trim() || null,
          replyTo: form.replyTo.trim() || null,
          verifyTls: form.verifyTls,
        },
      }),
    onSuccess: (v) => {
      qc.setQueryData(KEY, v);
      setForm((f) => ({ ...f, password: '' }));
      toast.success('Gespeichert – bitte eine Testnachricht senden');
    },
    onError,
  });
  const test = useMutation({
    mutationFn: () => api<{ ok: boolean; message: string }>('/tenant/mail-settings/test', { method: 'POST', body: { to: testTo } }),
    onSuccess: (r) => {
      if (r.ok) toast.success(r.message);
      else toast.error(r.message, { duration: 12_000 });
      qc.invalidateQueries({ queryKey: KEY });
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api<MailView>('/tenant/mail-settings', { method: 'DELETE' }),
    onSuccess: (v) => {
      qc.setQueryData(KEY, v);
      toast.success('SMTP-Einstellungen entfernt');
    },
    onError,
  });

  if (isLoading || !data) return <Skeleton className="h-96" />;
  const route = ROUTE_TEXT[data.route];

  return (
    <div className="mx-auto max-w-3xl">
      {confirmDialog}
      <PageHeader title="E-Mail-Versand" description="Absender für Einladungen, Passwort-Links, Ticket- und Reparaturbenachrichtigungen." />
      <div className="grid gap-6">
        <Alert variant={data.route === 'none' ? 'destructive' : 'default'}>
          <p className="flex flex-wrap items-center gap-2">
            <Badge variant={route.variant}>{data.route === 'none' ? 'Nicht eingerichtet' : 'Aktiv'}</Badge> {route.text}
          </p>
          {data.isPlatform && <p className="mt-2 text-sm">Dies ist der Plattform-Mandant: Der SMTP-Server hier gilt als Vorgabe für alle Mandanten ohne eigene Einstellungen.</p>}
          <p className="mt-2 text-sm">
            Mails aus einem Microsoft-365-Postfach versenden?{' '}
            <Link to="/admin/integrations/m365" className="font-medium text-primary hover:underline">
              Microsoft 365 einrichten
            </Link>
          </p>
        </Alert>

        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle>SMTP-Server</CardTitle>
              <CardDescription>Zugangsdaten Ihres Mailanbieters. Das Passwort wird verschlüsselt gespeichert und nicht wieder angezeigt.</CardDescription>
            </div>
            {s && (s.status === 'OK' ? <Badge variant="success">Geprüft</Badge> : s.status === 'ERROR' ? <Badge variant="destructive">Fehler</Badge> : <Badge variant="outline">Nicht geprüft</Badge>)}
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate();
              }}
            >
              {!s && (
                <Field label="Vorlage (optional)" htmlFor="m-preset">
                  <Select
                    id="m-preset"
                    value=""
                    onChange={(e) => {
                      const p = PRESETS.find((x) => x.label === e.target.value);
                      if (p) setForm((f) => ({ ...f, host: p.host || f.host, port: p.port, security: p.security }));
                    }}
                  >
                    <option value="">– Anbieter wählen –</option>
                    {PRESETS.map((p) => (
                      <option key={p.label} value={p.label}>
                        {p.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <div className="grid gap-4 sm:grid-cols-[1fr_7rem_10rem]">
                <Field label="Server" htmlFor="m-host">
                  <Input id="m-host" required value={form.host} onChange={(e) => set('host', e.target.value)} placeholder="smtp.example.de" autoComplete="off" />
                </Field>
                <Field label="Port" htmlFor="m-port">
                  <Input id="m-port" type="number" required min={1} max={65535} value={form.port} onChange={(e) => set('port', Number(e.target.value))} />
                </Field>
                <Field label="Verschlüsselung" htmlFor="m-sec">
                  <Select
                    id="m-sec"
                    value={form.security}
                    onChange={(e) => {
                      const sec = e.target.value as Security;
                      setForm((f) => ({ ...f, security: sec, port: sec === 'TLS' && f.port === 587 ? 465 : sec === 'STARTTLS' && f.port === 465 ? 587 : f.port }));
                    }}
                  >
                    <option value="STARTTLS">STARTTLS</option>
                    <option value="TLS">SSL/TLS</option>
                    <option value="NONE">keine</option>
                  </Select>
                </Field>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Benutzername" htmlFor="m-user">
                  <Input id="m-user" value={form.username} onChange={(e) => set('username', e.target.value)} autoComplete="off" />
                </Field>
                <Field label="Passwort" htmlFor="m-pass" hint={s?.hasPassword ? 'Gespeichert. Nur zum Ändern ausfüllen.' : undefined}>
                  <Input id="m-pass" type="password" value={form.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" placeholder={s?.hasPassword ? '•••••••• (unverändert)' : ''} />
                </Field>
                <Field label="Absenderadresse" htmlFor="m-from">
                  <Input id="m-from" type="email" required value={form.fromAddress} onChange={(e) => set('fromAddress', e.target.value)} placeholder="service@ihre-firma.de" />
                </Field>
                <Field label="Absendername" htmlFor="m-fromname" hint="Leer = Firmenname">
                  <Input id="m-fromname" value={form.fromName} onChange={(e) => set('fromName', e.target.value)} />
                </Field>
                <Field label="Antwortadresse (optional)" htmlFor="m-reply">
                  <Input id="m-reply" type="email" value={form.replyTo} onChange={(e) => set('replyTo', e.target.value)} />
                </Field>
                <label className="flex items-center gap-3 self-end rounded-md border p-2.5 text-sm">
                  <Switch checked={form.verifyTls} onCheckedChange={(v) => set('verifyTls', v)} /> Zertifikat des Servers prüfen
                </label>
              </div>
              {data.settings?.security === 'NONE' || form.security === 'NONE' ? <Alert variant="destructive">Ohne Verschlüsselung werden Zugangsdaten und Mails im Klartext übertragen – nur für interne Mailserver verwenden.</Alert> : null}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" loading={save.isPending} disabled={!form.host || !form.fromAddress}>
                  <Mail /> Speichern
                </Button>
                {s && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-destructive hover:text-destructive sm:ml-auto"
                    onClick={async () =>
                      (await confirm({
                        title: 'SMTP-Einstellungen entfernen?',
                        description: data.isPlatform ? 'Mandanten ohne eigene Einstellungen können dann keine Mails mehr senden.' : 'Danach gilt – falls vorhanden – die Vorgabe der Plattform.',
                        confirmLabel: 'Entfernen',
                        destructive: true,
                      })) && remove.mutate()
                    }
                  >
                    <Trash2 /> Entfernen
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {s && (
          <Card>
            <CardHeader>
              <CardTitle>Testnachricht</CardTitle>
              <CardDescription>Prüft Verbindung und Anmeldung und sendet eine kurze Nachricht.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3">
              <form
                className="flex flex-wrap gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  test.mutate();
                }}
              >
                <Input className="w-full sm:w-80" type="email" required placeholder="empfaenger@example.de" value={testTo} onChange={(e) => setTestTo(e.target.value)} aria-label="Empfänger" />
                <Button type="submit" variant="outline" loading={test.isPending} disabled={!testTo}>
                  <Send /> Senden
                </Button>
              </form>
              {s.lastTestAt && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  {s.status === 'OK' && <CheckCircle2 className="size-4 text-success" />}
                  Zuletzt geprüft: {formatDateTime(s.lastTestAt)}
                </p>
              )}
              {s.status === 'ERROR' && s.lastError && <Alert variant="destructive">{s.lastError}</Alert>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
